import type { Cloneable } from '@bindoc-projects/util-domain-lib';
import { BdEquals } from '@bindoc-projects/util-domain-lib';

export class AysClinicIdentifier implements Cloneable<AysClinicIdentifier> {
  /**
   * 261401030_0_2016 --> { ik: 261401030, sn: 0, year: 2016}
   * 261401030_0 --> { ik: 261401030, sn: 0}
   */
  public static fromString(idString: string): AysClinicIdentifier {
    const strParts = idString.split('_');
    const year = strParts[2] ? parseInt(strParts[2], 10) : undefined;

    return new AysClinicIdentifier(strParts[0], strParts[1], year);
  }

  /**
   * Mirror image of `AysClinicSnIdentifier.fromLegacy`: rebuild the legacy
   * identity from an SN ref (BDA-5809 step 3).
   *
   * Lossless for the shape UI layers need, because it reproduces exactly the
   * convention the SN mappers have used since the DE flip — `(clinicIk,
   * clinicSn)` — putting the opaque clinicSn in the `sn` slot and the display
   * `ik` in `ik`. Use this where a component keeps a legacy-typed public surface
   * while consuming the SN-typed `ClinicReadService`.
   */
  public static fromSnRef(ref: {
    clinicSn: string;
    year?: number;
    ik?: string;
  }): AysClinicIdentifier {
    return new AysClinicIdentifier(ref.ik ?? '', ref.clinicSn, ref.year);
  }

  public static fromObj(obj: {
    ik: string | number;
    sn: string | number;
    year?: string | number;
  }): AysClinicIdentifier {
    return new AysClinicIdentifier(
      obj.ik.toString(),
      obj.sn.toString(),
      obj.year ? parseInt(obj.year.toString(), 10) : undefined,
    );
  }

  /**
   * Read-boundary shim for a clinic node in an API response, tolerant of BOTH
   * identity shapes.
   *
   * The clinics-api flattened the nested `id: {ik, sn, year}` into the
   * `clinicSn`/`clinicIk`/`internalSn`/`year` quartet. That change is live on
   * the SN backend, but the same shared transformations also run against
   * backends still on the nested shape (e.g. the `edge-api-gateway`
   * `khbenchgateway` surface used by hartmann and admin, verified 2026-08-06).
   * So this accepts either and is the single entry point for both.
   *
   * On the flat shape, identity is rebuilt from `internalSn` — which is exactly
   * the pre-flatten `id.sn`: the 9-digit Standortnummer for iNEK sites and the
   * small ordinal for legacy sites (verified against `POST /clinics/by-condition`:
   * `L-260660839-2` → `internalSn: 2`). Mapping it back therefore reproduces the
   * **exact** identity string that existed before the flattening, which is what
   * every persisted `ik_sn` payload and every legacy analytics backend expects.
   * Falls back to the opaque `clinicSn` only when `internalSn` is absent.
   *
   * The nested object went by two names depending on the endpoint — `id` on
   * `ClinicIndexItem.clinic`, `clinicId` on `TopClinicResponse.clinicLocation`
   * — so both are accepted.
   *
   * Once every backend serves the flat shape, the nested branch can go.
   */
  public static fromClinicNode(obj: {
    clinicSn?: string;
    clinicIk?: string | number;
    internalSn?: string | number;
    year?: string | number;
    id?: { ik: string | number; sn: string | number; year?: string | number };
    clinicId?: {
      ik: string | number;
      sn: string | number;
      year?: string | number;
    };
  }): AysClinicIdentifier {
    if (obj.clinicSn === undefined || obj.clinicSn === null) {
      return AysClinicIdentifier.fromObj(obj.id ?? obj.clinicId);
    }

    const sn = obj.internalSn ?? obj.clinicSn;

    return new AysClinicIdentifier(
      obj.clinicIk !== undefined && obj.clinicIk !== null
        ? obj.clinicIk.toString()
        : '',
      sn.toString(),
      obj.year ? parseInt(obj.year.toString(), 10) : undefined,
    );
  }

  constructor(
    public ik: string,
    public sn: string,
    public year?: number,
  ) {}

  public toString(): string {
    let str = null;

    if (!!this.ik && this.sn !== null && this.sn !== undefined) {
      str = `${this.ik}_${this.sn}`;

      if (this.year) {
        str = `${str}_${this.year}`;
      }
    }

    return str;
  }

  public clone(): AysClinicIdentifier {
    return new AysClinicIdentifier(this.ik, this.sn, this.year);
  }

  public equals(other: AysClinicIdentifier): boolean {
    return !!this && !!other && this.toString() === other.toString();
  }

  public equalsByIkAndSn(other: AysClinicIdentifier): boolean {
    return (
      BdEquals.equals(this.ik, other.ik) && BdEquals.equals(this.sn, other.sn)
    );
  }
}
